import React, { useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import { getCableDisplayName, getMultipolarCablesDisplayNames, getTerminalGroupsForCables } from '../../utils/cableLogic';
import { validateCable } from '../../utils/validation';
import CableTessera from './CableTessera';
import MultipleGroupBox from './MultipleGroupBox';

export const MacroTessera = ({
  siglatura,
  groupData,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  settings,
  isDragEnabled,
  displaySiglatura,
  hoveredCable,
  setHoveredCable,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex,
  dragOverIndex,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButtons,
  onToggleDelete,
  isSelectionModeEnabled,
  availableSheaths = [],
  selectedCables = new Set(),
  onToggleSelection
}) => {
  const { t } = useI18n();
  const { cables, isMultiple, isSheathGroup, sheathId } = groupData;

  const hasIssues = cables.some(cable => {
    const validation = validateCable(cable, cables, settings.lingua, settings.useAWG, settings);
    return !validation.isValid;
  });

  const sheath = isSheathGroup && sheathId ? availableSheaths.find(s => s.id === sheathId) : null;

  // I morsetti condivisi si calcolano sull'intero gruppo, non sul singolo cavo.
  const terminalGroups = useMemo(
    () => getTerminalGroupsForCables(cables, settings),
    [cables, settings]
  );

  if (!isMultiple || cables.length === 1) {
    return (
      <CableTessera
        cable={cables[0]}
        validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
        displaySiglatura={displaySiglatura || getCableDisplayName(cables[0], settings.lingua, settings)}
        onCableClick={onCableClick}
        onEditCable={onEditCable}
        onEditMultipolarCable={onEditMultipolarCable}
        onDeleteCable={onDeleteCable}
        onDeleteMultipolarCable={onDeleteMultipolarCable}
        showSiglatura={true}
        clickable={false}
        settings={settings}
        isDragEnabled={isDragEnabled}
        editingIndex={editingIndex}
        editingMultipolarIndex={editingMultipolarIndex}
        showDeleteButton={showDeleteButtons.has(cables[0].originalIndex)}
        onToggleDelete={() => onToggleDelete(cables[0].originalIndex)}
        isSelectionModeEnabled={isSelectionModeEnabled}
        selectedCables={selectedCables}
        onToggleSelection={onToggleSelection}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragOver={handleDragOver}
        handleDragEnter={handleDragEnter}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        draggedIndex={draggedIndex}
        dragOverIndex={dragOverIndex}
        availableSheaths={availableSheaths}
        relatedCables={cables}
        terminalGroups={terminalGroups}
      />
    );
  }

  const getMacroTesseraTitle = () => {
    if (isSheathGroup && sheath) {
      return sheath.fullLabel || `${t('cableList.sheathLabel')} ${sheath.label}`;
    }

    const multipolarNames = getMultipolarCablesDisplayNames(cables, settings.lingua);
    if (multipolarNames.length > 0) {
      return multipolarNames.join(', ');
    }

    if (settings.marcaturePerCollegamento) {
      const cableNumbers = cables.map(cable => `#${cable.originalIndex + 1}`).join(', ');
      const numbersLabel = cables.length > 1 ? t('cableList.cablesLabel') : t('cableList.cableLabel');
      return `${numbersLabel} ${cableNumbers}`;
    }

    return `${t('cableList.cableLabel')} ${displaySiglatura}` || `${t('cableList.cableLabel')} ${siglatura}`;
  };

  const allSelected = cables.every(cable => selectedCables.has(cable.originalIndex));
  const someSelected = cables.some(cable => selectedCables.has(cable.originalIndex));

  return (
    <MultipleGroupBox
      title={getMacroTesseraTitle()}
      count={cables.length}
      isSheathGroup={isSheathGroup}
      hasIssues={hasIssues}
      isSelectionModeEnabled={isSelectionModeEnabled}
      allSelected={allSelected}
      someSelected={someSelected}
      onToggleSelection={() => {
        cables.forEach(cable => {
          if (allSelected) {
            onToggleSelection(cable.originalIndex);
          } else if (!selectedCables.has(cable.originalIndex)) {
            onToggleSelection(cable.originalIndex);
          }
        });
      }}
      onClick={() => onCableClick && onCableClick(siglatura, cables[0].originalIndex)}
    >
      {cables.map((cable) => {
        const isGlobalDragging = draggedIndex === cable.originalIndex;
        const isGlobalDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
        const isHovered = hoveredCable === cable.originalIndex;
        const validation = validateCable(cable, cables, settings.lingua, settings.useAWG, settings);
        const hasInnerIssues = !validation.isValid;

        return (
          <div
            key={cable.originalIndex}
            className="relative"
            draggable={isDragEnabled}
            onDragStart={(e) => handleDragStart(e, cable.originalIndex)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, cable.originalIndex)}
            onDragEnter={(e) => handleDragEnter(e, cable.originalIndex)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, cable.originalIndex)}
            onMouseEnter={() => !isDragEnabled && setHoveredCable?.(cable.originalIndex)}
            onMouseLeave={() => !isDragEnabled && setHoveredCable?.(null)}
          >
            {isDragEnabled && (
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-green-600 transition-colors z-10">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                </svg>
              </div>
            )}

            <CableTessera
              cable={cable}
              validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
              displaySiglatura={getCableDisplayName(cable, settings.lingua, settings)}
              onCableClick={null}
              onEditCable={onEditCable}
              onEditMultipolarCable={onEditMultipolarCable}
              onDeleteCable={onDeleteCable}
              onDeleteMultipolarCable={onDeleteMultipolarCable}
              showSiglatura={isSheathGroup ? true : settings.marcaturePerCollegamento}
              clickable={false}
              settings={settings}
              isDragEnabled={isDragEnabled}
              isDragging={isGlobalDragging}
              isDragOver={isGlobalDragOver}
              isHovered={isHovered}
              hasIssues={hasInnerIssues}
              editingIndex={editingIndex}
              editingMultipolarIndex={editingMultipolarIndex}
              showDeleteButton={showDeleteButtons && showDeleteButtons.has(cable.originalIndex)}
              onToggleDelete={() => onToggleDelete && onToggleDelete(cable.originalIndex)}
              isSelectionModeEnabled={isSelectionModeEnabled}
              selectedCables={selectedCables}
              onToggleSelection={onToggleSelection}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              draggedIndex={draggedIndex}
              dragOverIndex={dragOverIndex}
              availableSheaths={availableSheaths}
              relatedCables={cables}
              terminalGroups={terminalGroups}
              hideSheathBadge={isSheathGroup}
            />
          </div>
        );
      })}
    </MultipleGroupBox>
  );
};
